import React, { useMemo } from 'react';
import { StatFilterType, useSearch } from '../../contexts/SearchContext';
import { PatientListItemType } from '../../providers/patient/PatientsListProvider';
import { usePatientMetadata } from '../../providers/PatientMetadataProvider';
import { hasActionRequired, isInactiveStatus, isOnHold } from '../../utils/constants';

interface StatCardConfig {
    label: string;
    unit: string;
    filterKey: StatFilterType;
    statKey: keyof PatientStats;
    icon: React.ReactNode;
}

interface PatientStats {
    active: number;
    actionRequired: number;
    unreadMessages: number;
    onHold: number;
    inactive: number;
}

interface StatCardProps extends StatCardConfig {
    count: number;
    activeFilter: StatFilterType;
    onSelect: (filter: StatFilterType) => void;
}

const StatCard: React.FC<StatCardProps> = ({
    label,
    unit,
    count,
    filterKey,
    icon,
    activeFilter,
    onSelect
}) => {
    const isActive = filterKey === null ? activeFilter === null : activeFilter === filterKey;

    const handleClick = () => {
        onSelect(filterKey === null || isActive ? null : filterKey);
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className={`group flex-1 min-w-0 flex flex-col justify-center px-5 py-3.5 rounded-xl border-[1.5px] text-left cursor-pointer transition-all duration-200 hover:-translate-y-px active:translate-y-0 ${
                isActive
                    ? 'bg-yellow-50 border-yellow-400 shadow-sm'
                    : 'bg-white border-gray-200 shadow-sm hover:shadow hover:border-gray-300'
            }`}
        >
            <div className="flex items-center gap-2 mb-1">
                <span
                    className={`flex-shrink-0 transition-colors duration-200 ${
                        isActive ? 'text-yellow-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                >
                    {icon}
                </span>
                <span
                    className={`text-[11px] font-semibold tracking-[0.04em] uppercase transition-colors duration-200 ${
                        isActive ? 'text-gray-700' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                >
                    {label}
                </span>
            </div>
            <div className="flex items-baseline gap-1.5 pl-[22px]">
                <span className="text-[26px] font-bold leading-none text-gray-800 tabular-nums">
                    {count}
                </span>
                <span className="text-xs text-gray-400 font-medium">{unit}</span>
            </div>
        </button>
    );
};

// Referenced by PatientTable's empty state, which tells the user which card to
// open - renaming the card there and not here would rot the instruction.
export const INACTIVE_CARD_LABEL = 'Inactive';

const CARD_CONFIGS: StatCardConfig[] = [
    {
        label: 'Active Patients',
        unit: 'active',
        filterKey: null,
        statKey: 'active',
        icon: (
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        )
    },
    {
        label: 'Actions Required',
        unit: 'patients',
        filterKey: 'action_required',
        statKey: 'actionRequired',
        icon: (
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
        )
    },
    {
        label: 'New Messages',
        unit: 'unread',
        filterKey: 'unread_messages',
        statKey: 'unreadMessages',
        icon: (
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
        )
    },
    {
        label: 'On Hold Cases',
        unit: 'on hold',
        filterKey: 'on_hold',
        statKey: 'onHold',
        icon: (
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
        )
    },
    {
        label: INACTIVE_CARD_LABEL,
        unit: 'cases',
        filterKey: 'inactive',
        statKey: 'inactive',
        icon: (
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <rect x="2" y="4" width="20" height="5" rx="1" />
                <path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" />
                <line x1="10" y1="13" x2="14" y2="13" />
            </svg>
        )
    }
];

interface StatsBarProps {
    patients: PatientListItemType[];
}

const StatsBar: React.FC<StatsBarProps> = ({ patients }) => {
    const { activeStatFilter, setActiveStatFilter } = useSearch();
    const { unreadPatientIds } = usePatientMetadata();

    // Every count except "Inactive" is scoped to the active roster: an inactive
    // patient's leftover action or unread thread isn't work the practice still
    // has to do. PatientTable keeps its escape hatches off whenever a card is
    // selected, so each card's count matches the rows that card renders.
    const stats = useMemo((): PatientStats => {
        const activePatientIds = new Set<string>();
        let active = 0;
        let actionRequired = 0;
        let onHold = 0;
        let inactive = 0;

        for (const patient of patients) {
            if (isInactiveStatus(patient.treatmentStatus)) {
                inactive += 1;
                continue;
            }
            active += 1;
            if (patient.id) {
                activePatientIds.add(patient.id);
            }
            if (hasActionRequired(patient.requiredAction)) {
                actionRequired += 1;
            }
            if (isOnHold(patient.treatmentStatus)) {
                onHold += 1;
            }
        }

        let unreadMessages = 0;
        for (const id of unreadPatientIds) {
            if (activePatientIds.has(id)) {
                unreadMessages += 1;
            }
        }

        return { active, actionRequired, unreadMessages, onHold, inactive };
    }, [patients, unreadPatientIds]);

    return (
        <div className="flex items-stretch gap-3 px-8">
            {CARD_CONFIGS.map((config) => (
                <StatCard
                    key={config.filterKey ?? 'active'}
                    {...config}
                    count={stats[config.statKey]}
                    activeFilter={activeStatFilter}
                    onSelect={setActiveStatFilter}
                />
            ))}
        </div>
    );
};

export default StatsBar;
